import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import { useDeleteUser } from "../api/roster.mutations";

export function DeleteUserDialog({
  userId,
  name,
}: {
  userId: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  const deleteUser = useDeleteUser();
  const { showApiError } = useErrorDialog();

  const onConfirm = () =>
    deleteUser.mutate(userId, {
      onSuccess: () => setOpen(false),
      onError: (err) => showApiError(err, { title: "Kunde inte ta bort" }),
    });

  return (
    // Stay open (spinner on the confirm button) until the request settles; close only on success —
    // mirrors the raid/absence dialogs. The pending guard blocks Escape/Cancel while deleting.
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!deleteUser.isPending) setOpen(next);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          aria-label={`Ta bort ${name}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Ta bort {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Detta går inte att ångra. Användaren och all deras frånvaro tas bort
            permanent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteUser.isPending}>
            Avbryt
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={deleteUser.isPending}
          >
            <Trash2 />
            Ta bort
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
